"use client";

import { type MouseEvent, type ReactNode } from "react";

import { hasBrowserAnalyticsConsent } from "~/lib/consent-region";
import { capturePostHogEvent } from "~/lib/posthog/client";
import { storeSpecialtyDirectoryBookingAttribution } from "~/lib/client/specialty-directory-booking-attribution";

import { type DiscoverySurface } from "./types";

interface TherapistCardAttributionProps {
  children: ReactNode;
  therapistId: string;
  therapistSlug: string;
  discoverySurface?: DiscoverySurface;
  specialtySlug?: string;
  resultRank?: number;
}

/**
 * Click-capture attribution carried over unchanged from `search-therapist-card.tsx`:
 * fires only for the anchor actually clicked (profile link vs. a day-tile
 * booking link), gated by analytics consent, and only when the page opted
 * into a discovery surface. Event property names stay snake_case to match
 * the existing PostHog schema.
 */
export function TherapistCardAttribution({
  children,
  therapistId,
  therapistSlug,
  discoverySurface,
  specialtySlug,
  resultRank,
}: TherapistCardAttributionProps) {
  const captureDiscoveryAction = (event: MouseEvent<HTMLDivElement>) => {
    if (!discoverySurface || !hasBrowserAnalyticsConsent()) return;

    const link =
      event.target instanceof Element
        ? event.target.closest<HTMLAnchorElement>("a[href]")
        : null;
    const href = link?.getAttribute("href");

    if (!href) return;

    const properties = {
      therapist_id: therapistId,
      therapist_slug: therapistSlug,
      source_surface: discoverySurface,
      specialty_slug: specialtySlug,
      result_rank: resultRank,
    };

    if (href.startsWith(`/terapeuti/${therapistSlug}/book`)) {
      if (discoverySurface === "specialty_directory" && specialtySlug) {
        storeSpecialtyDirectoryBookingAttribution({
          specialtySlug,
          therapistId,
          createdAt: Date.now(),
        });
      }
      capturePostHogEvent("therapist_booking_started", properties);
      return;
    }

    if (href.startsWith(`/terapeuti/${therapistSlug}`)) {
      capturePostHogEvent("therapist_profile_opened", properties);
    }
  };

  return <div onClickCapture={captureDiscoveryAction}>{children}</div>;
}
